/* ============================================================================
 * 全局命令面板（⌘K / Ctrl+K）
 *   F-53  V2 无全局命令面板 —— 首页有一个"命令面输入"雏形但只在首页可用，
 *         27 页系统跨页只能靠左侧手风琴。
 *   F-59  V2 无跨页全局搜索 —— 订单号/工厂/物料无法全局检索。
 * 本组件同时承担"跳转"与"检索"两个职责，检索范围覆盖页面、基地、客户、产品、术语。
 * ========================================================================== */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FLAT_NAV } from '@/app/nav';
import { useSession, useRole } from '@/store/session';
import { FACTORIES, CUSTOMERS, PRODUCTS, MATERIALS, GLOSSARY } from '@/domain/master';
import { Icon, type IconName } from './Icon';

interface Cmd {
  id: string;
  kind: '页面' | '基地' | '客户' | '产品' | '物料' | '术语';
  title: string;
  hint?: string;
  icon: IconName;
  haystack: string;
  run: () => void;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const navAllowed = useSession((s) => s.navAllowed);
  const role = useRole();

  /* 全局快捷键 */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
        setQ(''); setIdx(0);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 0); }, [open]);

  const commands = useMemo<Cmd[]>(() => {
    const go = (r: string) => () => { navigate(`/${r}`); setOpen(false); };
    const list: Cmd[] = [];

    for (const n of FLAT_NAV) {
      if (!navAllowed(n.route)) continue;
      list.push({
        id: `page:${n.route}`, kind: '页面', title: n.name,
        hint: n.groupName ?? undefined, icon: n.icon,
        haystack: [n.name, n.route, n.groupName, ...(n.keywords ?? [])].filter(Boolean).join(' ').toLowerCase(),
        run: go(n.route),
      });
    }
    for (const f of FACTORIES) {
      list.push({
        id: `plant:${f.code}`, kind: '基地', title: f.short, hint: `${f.name} · ${f.province}`,
        icon: 'factory', haystack: `${f.short} ${f.name} ${f.code} ${f.province} ${f.legal}`.toLowerCase(),
        run: () => { navigate(`/plant/${f.code}`); setOpen(false); },
      });
    }
    for (const c of CUSTOMERS) {
      list.push({
        id: `cust:${c.code}`, kind: '客户', title: c.name, hint: `${c.region} · 份额 ${c.share}%`,
        icon: 'users', haystack: `${c.name} ${c.code} ${c.region}`.toLowerCase(),
        run: () => { navigate(`/customer/${c.code}`); setOpen(false); },
      });
    }
    for (const p of PRODUCTS) {
      list.push({
        id: `prod:${p.code}`, kind: '产品', title: p.name, hint: p.spec,
        icon: 'box', haystack: `${p.name} ${p.code} ${p.spec} ${p.family}`.toLowerCase(),
        run: () => { navigate(`/master?entity=product&code=${p.code}`); setOpen(false); },
      });
    }
    for (const m of MATERIALS) {
      list.push({
        id: `mat:${m.code}`, kind: '物料', title: m.name, hint: `${m.price} 元/${m.unit}`,
        icon: 'db', haystack: `${m.name} ${m.code}`.toLowerCase(),
        run: () => { navigate(`/material?code=${m.code}`); setOpen(false); },
      });
    }
    for (const [k, g] of Object.entries(GLOSSARY)) {
      list.push({
        id: `term:${k}`, kind: '术语', title: g.name, hint: g.text.slice(0, 42) + '…',
        icon: 'info', haystack: `${k} ${g.name} ${g.text}`.toLowerCase(),
        run: () => { navigate(`/master?glossary=${encodeURIComponent(k)}`); setOpen(false); },
      });
    }
    return list;
  }, [navigate, navAllowed, role]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return commands.filter((c) => c.kind === '页面').slice(0, 9);
    return commands.filter((c) => c.haystack.includes(s)).slice(0, 24);
  }, [q, commands]);

  useEffect(() => { setIdx(0); }, [q]);

  if (!open) return null;

  return (
    <div className="scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <div className="cmdk" role="dialog" aria-modal="true" aria-label="全局命令面板">
        <div className="cmdk-input">
          <Icon name="search" size={16} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="搜索页面、基地、客户、产品、物料、术语…"
            aria-label="全局搜索"
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(i + 1, results.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
              else if (e.key === 'Enter') { e.preventDefault(); results[idx]?.run(); }
            }}
          />
          <kbd>ESC</kbd>
        </div>
        <ul className="cmdk-list" role="listbox">
          {results.length === 0 && <li className="cmdk-empty">没有匹配结果</li>}
          {results.map((c, i) => (
            <li key={c.id}>
              <button
                type="button"
                className={`cmdk-item${i === idx ? ' on' : ''}`}
                role="option"
                aria-selected={i === idx}
                onMouseEnter={() => setIdx(i)}
                onClick={c.run}
              >
                <Icon name={c.icon} size={15} />
                <span className="cmdk-title">{c.title}</span>
                {c.hint && <span className="cmdk-hint ellip">{c.hint}</span>}
                <span className="cmdk-kind">{c.kind}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
